<template>
  <ToolShell
    tool-name="Consulting rate calculator"
    h1="Consulting rate calculator for independent consultants."
    lede="A consulting rate is your income goal plus overhead and profit, divided by the hours you can actually bill. Enter those numbers to get the hourly rate, day rate, and minimum project fee you need to charge. Free, instant, no signup."
    :trust="['No signup', 'Instant results', 'Hourly, day, and project rates']"
    form-title="Your numbers"
    :upsell-points="[
      'Proposals go out priced from the rate you just set',
      'Invoices collect online with payment links',
      'Payment reminders and profitability reporting run themselves',
    ]"
    free-label="The free calculator"
    :free-bullets="[
      'The rate for one income goal, calculated once',
      'You build the proposal and chase each payment yourself',
      'No record of whether the rate actually held up per client',
    ]"
    upgrade-headline="This number works once. Workspace369 makes it a workflow."
    :connected-bullets="[
      'Proposals priced from your rate send from the client record',
      'Approved proposals convert to invoices in one click',
      'Payment links collect online and reminders run on a schedule',
      'Profitability reporting shows what each client really pays',
    ]"
    slug="/tools/consulting-rate-calculator"
    :meta-description="metaDescription"
    doc-brand="Consulting rate calculator"
    :doc-subtitle="capacitySummary"
    doc-kind="SUMMARY"
    stamp="Result"
  >
    <template #form>
      <label class="ta-field">
        <span>Target annual income ($)</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5v13M11.5 4.5c-.6-1.2-1.9-1.8-3.5-1.8-1.9 0-3.2.9-3.2 2.4 0 3.4 7 1.7 7 5.1 0 1.6-1.5 2.5-3.8 2.5-1.8 0-3.2-.8-3.8-2" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          <input v-model.number="form.income" type="number" min="0" step="5000" placeholder="120000" />
        </div>
      </label>

      <label class="ta-field">
        <span>Billable hours per week</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M8 4.8V8l2.3 1.6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          <input v-model.number="form.hoursPerWeek" type="number" min="0" max="80" step="1" placeholder="25" />
        </div>
      </label>

      <label class="ta-field">
        <span>Weeks worked per year</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><rect x="2" y="3" width="12" height="11" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          <input v-model.number="form.weeks" type="number" min="0" max="52" step="1" placeholder="46" />
        </div>
      </label>

      <label class="ta-field">
        <span>Overhead (%)</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M12.5 3.5l-9 9" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><circle cx="4.8" cy="4.8" r="1.8" fill="none" stroke="currentColor" stroke-width="1.4"/><circle cx="11.2" cy="11.2" r="1.8" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>
          <input v-model.number="form.overhead" type="number" min="0" max="100" step="5" placeholder="15" />
        </div>
      </label>

      <label class="ta-field">
        <span>Profit margin (%)</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M12.5 3.5l-9 9" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><circle cx="4.8" cy="4.8" r="1.8" fill="none" stroke="currentColor" stroke-width="1.4"/><circle cx="11.2" cy="11.2" r="1.8" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>
          <input v-model.number="form.margin" type="number" min="0" max="90" step="5" placeholder="20" />
        </div>
      </label>
      <p v-show="!result" class="ta-doc-note" role="status">Enter valid planning inputs to see a rate. A dash means the estimate is unavailable.</p>
    </template>

    <template #preview>
      <div class="ta-doc-meta">
        <div><span>Income goal</span><strong>{{ money(form.income) }}</strong></div>
        <div><span>Billable per week</span><strong>{{ form.hoursPerWeek || 0 }}h</strong></div>
        <div><span>Weeks per year</span><strong>{{ form.weeks || 0 }}</strong></div>
        <div><span>Overhead + margin</span><strong>{{ form.overhead || 0 }}% + {{ form.margin || 0 }}%</strong></div>
      </div>

      <div class="ta-table-scroll" tabindex="0">
        <table class="ta-table">
          <thead>
            <tr><th>Rate breakdown</th><th>Value</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Income goal + {{ form.overhead || 0 }}% overhead</td>
              <td class="ta-num">{{ money(result?.loadedIncome) }}</td>
            </tr>
            <tr>
              <td>Revenue target after {{ form.margin || 0 }}% margin</td>
              <td class="ta-num">{{ money(result?.revenueTarget) }}</td>
            </tr>
            <tr>
              <td>Billable hours per year ({{ form.hoursPerWeek || 0 }}h × {{ form.weeks || 0 }} weeks)</td>
              <td class="ta-num">{{ billableHoursDisplay }}</td>
            </tr>
            <tr>
              <td>Day rate (8 hours)</td>
              <td class="ta-num">{{ money(result?.dayRate) }}</td>
            </tr>
            <tr>
              <td>Minimum project fee (10-hour block)</td>
              <td class="ta-num">{{ money(result?.projectFee) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="ta-doc-total">
        <span>Required hourly rate</span>
        <strong>{{ money(result?.hourlyRate) }}</strong>
      </div>

      <p class="ta-doc-note">Billable utilization is the lever most consultants underestimate.</p>
    </template>

    <template #steps>
      <div><span>01</span><div><h2>Set income and capacity</h2><p>Your annual income goal, billable hours per week, and weeks worked per year.</p></div></div>
      <div><span>02</span><div><h2>Load overhead and margin</h2><p>Overhead covers the cost of running the practice; margin is the profit on top.</p></div></div>
      <div><span>03</span><div><h2>Read the rates</h2><p>The hourly rate, day rate, and minimum project fee update live as you type.</p></div></div>
    </template>

    <ToolGuide :sections="guideSections" faq-title="Consulting rate questions" :faq="faq" />
  </ToolShell>
</template>

<script setup>
import ToolGuide from "~/components/tools/ToolGuide.vue";
import ToolShell from "~/components/tools/ToolShell.vue";
import { calculateConsultingRate } from "~/utils/consulting-rate.mjs";

const form = reactive({ income: 120000, hoursPerWeek: 25, weeks: 46, overhead: 15, margin: 20 });
const result = computed(() => calculateConsultingRate(form));

const billableHoursDisplay = computed(() =>
  result.value ? `${result.value.billableHours.toLocaleString("en-US")}h` : "—",
);

const money = (value) =>
  typeof value === "number" && Number.isFinite(value)
    ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "—";

const capacitySummary = computed(
  () => `${money(form.income)} goal · ${form.hoursPerWeek || 0}h/week billable`,
);

const guideSections = [
  {
    title: "How this calculator works out your rate",
    paragraphs: [
      "The calculator adds your overhead to the income goal, then grosses that up so your profit margin is a share of the final revenue rather than a markup on cost. It divides that revenue by your billable hours for the year to get the hourly rate. The day rate is eight billable hours and the minimum project fee is a ten-hour block.",
      "With the starting numbers, a $120,000 income goal plus 15% overhead is $138,000. A 20% margin on revenue makes the target $172,500. At 25 billable hours a week for 46 weeks, that is 1,150 hours, so the rate is $150.00 an hour, $1,200.00 a day and $1,500.00 as a minimum project fee.",
    ],
  },
  {
    title: "Why billable hours move the rate most",
    paragraphs: [
      "Billable hours are the divisor, so a small change there moves every rate. Keep the same goal, overhead and margin, drop to 20 billable hours a week, and the rate rises from $150.00 to $187.50 an hour, with a day rate of $1,500.00.",
      "Count only the hours a client pays for. Proposals, discovery calls, admin, travel, learning and the gaps between engagements are real work, but they are paid for by your rate, not billed on their own. If you are not sure of your number, track a few normal weeks before you settle on it.",
    ],
    links: [
      { label: "Billable utilization calculator", path: "/tools/billable-utilization-calculator" },
      { label: "Time tracking", path: "/features/time-tracking" },
    ],
  },
  {
    title: "Hourly, day rate or project fee: which to quote",
    paragraphs: [
      "The three numbers come from the same target, so pick the one that fits how the client buys.",
    ],
    bullets: [
      "Hourly: open-ended advice and small follow-ups, where the scope is hard to fix in advance.",
      "Day rate: workshops, audits and on-site days, where the client books a block of your time.",
      "Project fee: defined deliverables. Work out the hours you expect, price them at your hourly rate, add room for revisions, then quote one figure.",
      "Minimum fee: the smallest engagement worth taking on, so short requests still cover the time around them.",
      "Retainer: ongoing advice billed monthly for a set number of hours or an agreed scope.",
    ],
    links: [
      { label: "Retainer calculator", path: "/tools/retainer-calculator" },
      { label: "Fixed fee, hourly and retainer billing", path: "/blog/fixed-fee-vs-hourly-vs-retainer-billing" },
    ],
  },
  {
    title: "Three worked examples",
    paragraphs: [
      "Each of these can be reproduced by typing the same numbers above.",
    ],
    bullets: [
      "$90,000 goal, 20 hours a week, 44 weeks, 10% overhead, 15% margin: $132.35 an hour, $1,058.82 a day, $1,323.53 minimum project fee.",
      "$120,000 goal, 25 hours a week, 46 weeks, 15% overhead, 20% margin: $150.00 an hour, $1,200.00 a day, $1,500.00 minimum project fee.",
      "$200,000 goal, 30 hours a week, 46 weeks, 20% overhead, 25% margin: $231.88 an hour, $1,855.07 a day, $2,318.84 minimum project fee.",
    ],
  },
  {
    title: "How this differs from the hourly rate calculator",
    paragraphs: [
      "The hourly rate calculator takes business costs as a monthly dollar amount. This one takes overhead as a percentage of your income goal and adds a minimum project fee, which suits consultants who quote engagements more often than hours. Use whichever matches how you already track costs; the rates will be close if the inputs agree.",
    ],
    links: [
      { label: "Hourly rate calculator", path: "/tools/hourly-rate-calculator" },
      { label: "Published freelancer rate benchmarks", path: "/research/freelancer-rate-benchmarks" },
    ],
  },
  {
    title: "Using your rate in Workspace369",
    paragraphs: [
      "On every plan you can price proposals from your services catalog, track billable time against a client, run retainers with an hour bank, and put recurring invoices on card autopay. Project and customer profitability reports, which show whether the rate held up once the work was done, start on Specialist.",
    ],
    links: [
      { label: "How to invoice for consulting services", path: "/learn/how-to-invoice-for-consulting-services" },
      { label: "Consulting proposal template", path: "/learn/consulting-proposal-template" },
      { label: "Proposals and quotes", path: "/features/quoting" },
    ],
  },
];

const faq = [
  {
    q: "How do I calculate a consulting rate?",
    a: "Add your overhead to your annual income goal, divide by one minus your profit margin, then divide by the hours you expect to bill in a year. With a $120,000 goal, 15% overhead, a 20% margin and 1,150 billable hours, the rate is $150 an hour.",
  },
  {
    q: "How do I turn an hourly rate into a day rate?",
    a: "Multiply the hourly rate by the billable hours in the day you are selling. This calculator uses eight. If your client day includes travel or fewer working hours, agree what the day covers and price it from the hours you will actually bill.",
  },
  {
    q: "What should overhead include?",
    a: "Everything the business pays for that is not your own income: software, equipment, insurance, accounting, workspace, travel you cannot bill, and training. Enter it as a percentage of your income goal; if you know it as a monthly amount, the hourly rate calculator takes it that way.",
  },
  {
    q: "Should I show my rate on proposals?",
    a: "That is your choice. Many consultants quote a fixed project fee worked out from the rate, so the client compares outcomes rather than hours. If you bill hourly, show the rate and the expected hours so the total is easy to follow.",
  },
];

const metaDescription =
  "Free consulting rate calculator: enter income goals, billable hours, and weeks worked to get the hourly, day, and project rate. Instant, no signup.";

useHead(() => ({
  title: "Free Consulting Rate Calculator | Workspace369",
  meta: [
    {
      name: "description",
      content: metaDescription,
    },
    { name: "twitter:title", content: "Free Consulting Rate Calculator | Workspace369" },
    {
      name: "twitter:description",
      content: "Enter your income goal and billable capacity to get the hourly rate, day rate, and minimum project fee. Free, no signup.",
    },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: "Free Consulting Rate Calculator | Workspace369" },
    {
      property: "og:description",
      content: "Enter your income goal and billable capacity to get the hourly rate, day rate, and minimum project fee. Free, no signup.",
    },
  ],
}));
</script>
